import React, { useCallback } from 'react';

import Image, { type ImageProps, type StaticImageData } from 'next/image';

import ImageLoader from '@/common/components/image-loader';
import { useImageLoaderRef } from '@/common/hooks/use-image-loader-ref.hook';

interface CustomImageProps extends Omit<ImageProps, 'alt'> {
  hasFallback?: boolean;
  alt: string; // Make alt required
}

// Helper: Dev warnings for SEO issues
const logDevWarnings = (
  alt: string,
  src: string | StaticImageData | Record<string, any>,
  fill?: boolean,
  finalSizes?: string
) => {
  if (process.env.NODE_ENV !== 'development') return;

  const srcString =
    typeof src === 'string'
      ? src
      : typeof src === 'object' && src !== null && 'src' in src
        ? String(src.src)
        : '';

  // Warn about weak alt
  if (
    [
      'productImage',
      'image',
      'photo',
      'vendorLogo',
      'تصویر',
      'تصویر محصول',
      'لوگو فروشگاه',
    ].includes(alt)
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[CustomImage SEO] Weak alt: "${alt}"\nSource: ${srcString}\nConsider using descriptive alt text instead.`
    );
  }

  // Warn about fill without sizes
  if (fill && !finalSizes) {
    // eslint-disable-next-line no-console
    console.warn(
      `[CustomImage SEO] fill=true without sizes\nSource: ${srcString}\nDefaulting to sizes="100vw"`
    );
  }
};

/**
 * SEO-optimized CustomImage wrapper for next/image
 *
 * Features:
 * - Requires descriptive alt text for better SEO and accessibility
 * - Applies loading="lazy" by default (unless priority=true)
 * - Adds decoding="async" for non-blocking decode
 * - Adds fetchPriority="high" when priority=true
 * - Warns in dev when alt is poor or sizes is missing with fill
 * - Ensures fallback loader hides after image loads
 *
 * @see USAGE.md for best practices
 */
const CustomImage: React.FC<CustomImageProps> = ({
  hasFallback = true,
  alt,
  src,
  onLoad,
  loading,
  priority,
  decoding,
  fetchPriority,
  fill,
  sizes,
  width,
  height,
  ...rest
}) => {
  const { imageloaderRef, onLoadHandler } = useImageLoaderRef();

  const finalLoading = priority ? undefined : loading || 'lazy';
  const finalDecoding = decoding || 'async';
  const finalFetchPriority = priority ? fetchPriority || 'high' : fetchPriority;
  const finalSizes = fill && !sizes && !width && !height ? '100vw' : sizes;

  // Dev warnings
  logDevWarnings(alt, src, fill, finalSizes);

  const handleLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      onLoadHandler();
      onLoad?.(event);
    },
    [onLoadHandler, onLoad]
  );

  return (
    <>
      {hasFallback && (
        <div
          ref={imageloaderRef}
          className="h-full w-full"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
          }}
        >
          <ImageLoader />
        </div>
      )}
      <Image
        {...rest}
        src={src}
        alt={alt}
        width={width}
        height={height}
        fill={fill}
        sizes={finalSizes}
        loading={finalLoading}
        priority={priority}
        decoding={finalDecoding}
        fetchPriority={
          finalFetchPriority as 'high' | 'low' | 'auto' | undefined
        }
        onLoad={handleLoad}
        style={{
          position: fill ? 'absolute' : undefined,
          zIndex: 2,
          ...rest.style,
        }}
      />
    </>
  );
};

export default CustomImage;
